import type { HoldCollection, HoldGroup, HoldPayload } from '@holdthat/core';
import type { EncryptedEntityV1 } from '@holdthat/crypto';

type EntityIdentity = Pick<EncryptedEntityV1, 'entityID' | 'ownerID'>;

export function canonicalUUID(value: string): string {
  return value.toLowerCase();
}

function requireMatchingEntityID(payloadID: string, entityID: string): void {
  if (canonicalUUID(payloadID) !== canonicalUUID(entityID)) {
    throw new Error('Innere und äußere Entitäts-ID stimmen nicht überein.');
  }
}

function requireMatchingOwnerID(payloadOwnerID: string | null, entityOwnerID: string): void {
  if (payloadOwnerID && canonicalUUID(payloadOwnerID) !== canonicalUUID(entityOwnerID)) {
    throw new Error('Innere und äußere Owner-ID stimmen nicht überein.');
  }
}

/**
 * UUIDs encoded by native clients may contain uppercase hexadecimal digits,
 * while FastAPI serializes the authenticated outer identifiers in lowercase.
 * The signed outer identity remains authoritative after equivalence checking.
 */
export function holdWithAuthoritativeIdentity(payload: HoldPayload, entity: EntityIdentity): HoldPayload {
  requireMatchingEntityID(payload.id, entity.entityID);
  requireMatchingOwnerID(payload.ownerID, entity.ownerID);
  return {
    ...payload,
    id: entity.entityID,
    ownerID: entity.ownerID,
    attachments: payload.attachments.map((attachment) => ({ ...attachment, id: canonicalUUID(attachment.id) })),
    thumbnailAttachmentID: payload.thumbnailAttachmentID ? canonicalUUID(payload.thumbnailAttachmentID) : payload.thumbnailAttachmentID,
    collectionIDs: payload.collectionIDs.map(canonicalUUID),
    groupIDs: payload.groupIDs.map(canonicalUUID),
    needsSync: false,
  };
}

export function collectionWithAuthoritativeIdentity(payload: HoldCollection, entity: EntityIdentity): HoldCollection {
  requireMatchingEntityID(payload.id, entity.entityID);
  return { ...payload, id: entity.entityID, needsSync: false };
}

export function groupWithAuthoritativeIdentity(payload: HoldGroup, entity: EntityIdentity): HoldGroup {
  requireMatchingEntityID(payload.id, entity.entityID);
  return { ...payload, id: entity.entityID };
}

